import { AlertTriangle } from "../../../../components/icons";

interface StatusRibbonProps {
  t: any;
  currentStatusNormalized: string;
  currentPhase: string;
  clockMode: string;
  isGlobalClockRunning: boolean;
  cockpitLocked: boolean;
  lockReason?: string;
}

export default function StatusRibbon({
  t,
  currentStatusNormalized,
  currentPhase,
  clockMode,
  isGlobalClockRunning,
  cockpitLocked,
  lockReason,
}: StatusRibbonProps) {
  return (
    <>
      <div className="mt-3 max-[1180px]:landscape:mt-1.5 grid grid-cols-2 md:grid-cols-4 gap-2 max-[1180px]:landscape:gap-1 text-xs max-lg:flex max-lg:flex-wrap max-lg:gap-1.5 max-lg:mt-1.5">
        <div className="flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-1.5 max-[1180px]:landscape:px-2 max-[1180px]:landscape:py-1 border border-slate-700 max-lg:flex-[1.5] max-lg:min-w-max max-lg:justify-center max-lg:gap-1.5 max-lg:px-2 max-lg:py-1 max-lg:rounded-full max-lg:h-8">
          <span className="font-semibold text-slate-400 text-xs uppercase tracking-wide max-lg:text-[10px]">
            {t("statusLabel", "Status")}
          </span>
          <span className="inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold bg-primary-900/60 text-primary-300 border border-primary-700/40">
            {t(
              `statusValue.${currentStatusNormalized}`,
              currentStatusNormalized,
            )}
          </span>
        </div>
        <div className="flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-1.5 max-[1180px]:landscape:px-2 max-[1180px]:landscape:py-1 border border-slate-700 max-lg:flex-1 max-lg:min-w-0 max-lg:justify-center max-lg:gap-1.5 max-lg:px-2 max-lg:py-1 max-lg:rounded-full max-lg:h-8">
          <span className="font-semibold text-slate-400 text-xs uppercase tracking-wide max-lg:text-[10px]">
            {t("phaseLabel", "Phase")}
          </span>
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-emerald-900/60 text-emerald-300 border border-emerald-700/40">
            {t(`phaseValue.${currentPhase}`, currentPhase)}
          </span>
        </div>
        <div className="flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-1.5 max-[1180px]:landscape:px-2 max-[1180px]:landscape:py-1 border border-slate-700 max-lg:flex-1 max-lg:min-w-0 max-lg:justify-center max-lg:gap-1.5 max-lg:px-2 max-lg:py-1 max-lg:rounded-full max-lg:h-8">
          <span className="font-semibold text-slate-400 text-xs uppercase tracking-wide max-lg:text-[10px]">
            {t("clockModeLabel", "Clock Mode")}
          </span>
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-slate-700 text-slate-200 border border-slate-600">
            {t(`clockModeValue.${clockMode}`, clockMode)}
          </span>
        </div>
        <div className="flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-1.5 max-[1180px]:landscape:px-2 max-[1180px]:landscape:py-1 border border-slate-700 max-lg:flex-1 max-lg:min-w-0 max-lg:justify-center max-lg:gap-1.5 max-lg:px-2 max-lg:py-1 max-lg:rounded-full max-lg:h-8">
          <span className="font-semibold text-slate-400 text-xs uppercase tracking-wide max-lg:text-[10px]">
            {t("runningLabel", "Running")}
          </span>
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold border ${
              isGlobalClockRunning
                ? "bg-green-900/60 text-green-300 border-green-700/40"
                : "bg-red-900/60 text-red-300 border-red-700/40"
            }`}
          >
            {isGlobalClockRunning
              ? t("runningYes", "Yes")
              : t("runningNo", "No")}
          </span>
        </div>
      </div>

      {cockpitLocked && (
        <div
          className="mt-3 flex items-center gap-2 text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 shadow-sm"
          data-testid="cockpit-lock-banner"
        >
          <AlertTriangle size={16} className="shrink-0 text-amber-400" />
          <span>
            {/*
             * PMA-COCK-028 (2026-09-23): `lockBanner` is an UNREACHABLE
             * fallback today, not dead code that was left to rot — record
             * this explicitly so the wrong claim it used to make ("...
             * Fulltime...", corrected to "... Completed...") cannot
             * resurface silently. `cockpitLocked` (this banner's own
             * gating condition) is COMPLETED-only, and `lockReason` has
             * exactly one production assignment
             * (`useCockpitTransitionState.ts`'s `lockReasonCompleted`),
             * which is non-empty whenever `cockpitLocked` is true — so
             * `lockReason` never falls through to this fallback in
             * practice. It stays typed `string | undefined` at three prop
             * boundaries (`CockpitContext.tsx` -> `LoggerCockpit.tsx` ->
             * `CockpitTopSection.tsx`/`LoggerView.tsx` -> here), so ONE
             * optional-prop change upstream would make this fallback
             * reachable again — kept correct, not deleted, for exactly
             * that reason.
             */}
            {lockReason ||
              t(
                "lockBanner",
                "Match is closed (Completed). Editing is disabled.",
              )}
          </span>
        </div>
      )}
    </>
  );
}
